import { useContext, useState } from 'react';
import { Dropdown } from 'antd';
import {
  BodySubtitle,
  BodyTitle,
  InputTypeSelect,
  TeamPill,
  TeamPillContainer,
  TeamPillLabel,
} from './styles';
import { TeamsMenu } from 'components/teamSelect';
import { SessionContext } from 'contexts/session';
import './styles.scss';
import Icon from 'components/icons';
import Switch from 'components/switch';
import { TeamSelectProps } from './types';

const TeamSelector = ({
  teamsSelected,
  disabled,
  onTeamChange,
  withManagerSwitch,
  isManager,
  onSwitchChange,
  analyticsTheme,
  selectAllOption,
  containerStyles,
  wrapperStyles,
  bgColor,
  wideInput,
}: TeamSelectProps): JSX.Element => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [teamRemoved, setTeamRemoved] = useState<{ teamId: string } | null>(null);
  const { account, teams, isAccountOwner, isTeamManager } = useContext(SessionContext);

  const emptySelectionLabel = () => {
    if (selectAllOption) return selectAllOption;
    if (analyticsTheme) {
      return isAccountOwner ? 'All users from the account' : 'All my teams';
    }
    return 'No teams selected';
  };

  const showBodyTitles = !analyticsTheme;
  const oneTeamManager = isTeamManager && teams && teams.length === 1;

  const showEmptyLabel = () => {
    return teamsSelected.length === 0;
  };

  const handleTeamChange = (teamIds: string[]) => {
    const newTeams: any[] = [];
    teamIds.map((teamId) => {
      const newTeam = teams?.filter(({ id }) => id === teamId)?.[0];
      newTeams.push({
        id: teamId,
        name: newTeam?.name,
        icon: newTeam?.icon || null,
        slug: newTeam?.slug,
      });
    });
    onTeamChange(newTeams);
  };

  const handleTeamRemove = (teamId: string) => {
    // Fresh object reference each click so the consumer's effect fires
    // even when the same team is removed twice in a row.
    setTeamRemoved({ teamId });
  };

  if (!teams) return null;

  if (oneTeamManager)
    teamsSelected = [
      {
        team: teams[0],
        team_id: teams[0].id,
        name: teams[0].name,
        is_manager: isTeamManager,
      },
    ];

  const teamsForMenu = teams;

  const SelectedTeamsPillsList = (): JSX.Element => {
    return (
      <TeamPillContainer>
        {teamsSelected.map(({ team }) => (
          <TeamPill
            key={team?.id}
            closable={!oneTeamManager}
            onClose={() => handleTeamRemove(team?.id)}
            bgcolor={team?.icon?.color}
            closeIcon={<Icon icon={'Close'} size={'small'} />}
          >
            <TeamPillLabel>{team?.name}</TeamPillLabel>
          </TeamPill>
        ))}
      </TeamPillContainer>
    );
  };

  const stylesProp = containerStyles ?? {};

  return (
    <div style={{ ...wrapperStyles }}>
      {showBodyTitles && (
        <>
          <BodyTitle>Optional Settings</BodyTitle>
          <BodySubtitle>Invite to team</BodySubtitle>
        </>
      )}
      <div style={{ ...stylesProp, display: withManagerSwitch && 'flex' }}>
        <Dropdown
          overlay={
            <TeamsMenu
              analyticsTheme={analyticsTheme}
              accountTeams={teamsForMenu}
              onClose={() => setMenuVisible(false)}
              onTeamSelect={handleTeamChange}
              withSubmit={true}
              account={account}
              emptyLabel={'Invite to account (Without team)'}
              selectAllOption={selectAllOption}
              fromInvites={true}
              teamRemoved={teamRemoved}
              full
              teamsSelected={teamsSelected}
            />
          }
          trigger={['click']}
          visible={menuVisible && !oneTeamManager}
          onVisibleChange={setMenuVisible}
          disabled={oneTeamManager}
          placement={'bottomRight'}
        >
          <InputTypeSelect
            onClick={() => setMenuVisible(!disabled)}
            bgColor={bgColor}
            wideInput={wideInput}
            style={{
              cursor: disabled ? 'not-allowed' : 'pointer',
              ...(withManagerSwitch && { width: '50%' }),
            }}
          >
            {teamsSelected.length !== 0 && <SelectedTeamsPillsList />}
            <p>{showEmptyLabel && emptySelectionLabel()}</p>
            {!oneTeamManager && <Icon icon={'ChevronDown'} size={'smaller'} />}
          </InputTypeSelect>
        </Dropdown>
        {withManagerSwitch && (
          <Switch
            label={'Manager'}
            style={{ marginLeft: '12px' }}
            disabled={teamsSelected.length === 0}
            checked={isManager}
            onChange={(checked: boolean) => onSwitchChange?.(checked)}
          />
        )}
      </div>
    </div>
  );
};

export default TeamSelector;
